import React, { createContext, useContext, useState, useEffect } from "react";
import { useSeason } from "./SeasonContext";
import { usePersonnel } from "../hooks/usePersonnel";
import { Roster, RosterPlayer } from "../types/RosterTypes";
import { API_BASE_URL } from "../constants";
import axios from "axios";

export interface RosterContextType {
  selectedRoster: Roster | null;
  rosters: Roster[];
  rosterPlayers: RosterPlayer[];
  isLoading: boolean;
  error: string | null;
  setSelectedRoster: (roster: Roster | null) => void;
  fetchRosterPlayers: (rosterId: number) => Promise<void>;
  addRosterPlayer: (rosterId: number, playerData: any) => Promise<boolean>;
  removeRosterPlayer: (rosterId: number, playerId: number) => Promise<boolean>;
  updateRosterPlayer: (rosterId: number, playerData: any) => Promise<boolean>;
}

const RosterContext = createContext<RosterContextType | undefined>(undefined);

export const RosterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedSeason } = useSeason();
  const personnelController = usePersonnel();
  const [selectedRoster, setSelectedRoster] = useState<Roster | null>(null);
  const [rosters, setRosters] = useState<Roster[]>([]);
  const [rosterPlayers, setRosterPlayers] = useState<RosterPlayer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch rosters when season changes
  useEffect(() => {
    const fetchRosters = async () => {
      if (!selectedSeason) return;

      setIsLoading(true);
      setError(null);
      try {
        const fetchedRosters = await personnelController.fetchRostersBySeason(
          selectedSeason.id
        );
        setRosters(fetchedRosters);

        // Select first roster if none selected or if current selection is from different season
        if (!selectedRoster || selectedRoster.season_id !== selectedSeason.id) {
          const firstRoster = fetchedRosters[0];
          if (firstRoster) {
            setSelectedRoster(firstRoster);
            await fetchRosterPlayers(firstRoster.id);
          }
        }
      } catch (err) {
        setError("Failed to fetch rosters");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRosters();
  }, [selectedSeason]);

  const fetchRosterPlayers = async (rosterId: number) => {
    setIsLoading(true);
    setError(null);
    await axios
    .get(`${API_BASE_URL}/rosters/${rosterId}/roster_players/`)
    .then((response) => {
      setRosterPlayers(response.data);
      setIsLoading(false);
      return response.data;
    })
    .catch((err: any) => {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error fetching roster players"
      );
      console.error("Error fetching roster players:", err);
      return [];
    });
  };

  const addRosterPlayer = async (rosterId: number, playerData: any) => {
    try {
      const result = await personnelController.createOrUpdatePlayer(
        playerData,
        rosterId
      );
      if (result) {
        await fetchRosterPlayers(rosterId);
      }
      return !!result;
    } catch (err) {
      setError("Failed to add player to roster");
      console.error(err);
      return false;
    }
  };

  const removeRosterPlayer = async (rosterId: number, playerId: number) => {
    try {
      const success = await personnelController.removeRosterPlayer(
        rosterId,
        playerId
      );
      if (success) {
        await fetchRosterPlayers(rosterId);
      }
      return success;
    } catch (err) {
      setError("Failed to remove player from roster");
      console.error(err);
      return false;
    }
  };

  const updateRosterPlayer = async (rosterId: number, playerData: any) => {
    try {
      const success = await personnelController.updateRosterMembership(
        playerData.membershipId,
        {
          jersey_number: playerData.jerseyNumber,
          status: playerData.status,
        }
      );
      if (success) {
        await fetchRosterPlayers(rosterId);
      }
      return success;
    } catch (err) {
      setError("Failed to update player");
      console.error(err);
      return false;
    }
  };

  const value = {
    selectedRoster,
    rosters,
    rosterPlayers,
    isLoading,
    error,
    setSelectedRoster,
    fetchRosterPlayers,
    addRosterPlayer,
    removeRosterPlayer,
    updateRosterPlayer,
  };

  return (
    <RosterContext.Provider value={value}>{children}</RosterContext.Provider>
  );
};

export const useRoster = () => {
  const context = useContext(RosterContext);
  if (context === undefined) {
    throw new Error("useRoster must be used within a RosterProvider");
  }
  return context;
};
